import { Icon, InlineIcon } from "@iconify/react-with-api";
import { Grid, Link, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { format } from "d3-format";
import React from "react";
import useDimensions from "react-use-dimensions";
import capitaliseFirstLetter from "../helpers/capitaliseFirstLetter";
import { infoBoxDescriptions, summaryBoxes } from "../helpers/infoBoxDescriptions";
import SummaryMultiPieChart from "./SummaryMultiPieChart";
import SummaryProfileLineMarker from "./SummaryProfileLineMarker";
import SummaryRankingBarChart from "./SummaryRankingBarChart";
import SummaryStackedBarChart from "./SummaryStackedBarChart";

import extractTradingPartnerInfo from "../helpers/extractTradingPartnerInfo.js";

const useStyles = makeStyles({
  box: {
    padding: "30px !important",
  },
  title_box: {
    padding: "1rem",
    textAlign: "center",
    "& img": {
      display: "inline-block",
      marginTop: "7px",
    },
    "& h3": {
      color: "#ae8d6c",
      paddingLeft: "1rem",
      marginBottom: "0rem",
      marginTop: "0.5rem",
      fontWeight: "400",
      display: "inline-block",
      position: "relative",
      top: "-19px",
    },
    //backgroundColor: 'rgba(174,141,108,1)',
    backgroundImage: `url(${process.env.PUBLIC_URL}/tjn_yellow_corner_top.png)`,
    backgroundPosition: "left top",
    backgroundRepeat: "no-repeat",
    borderBottom: "2px solid rgba(80,128,94,1)",
  },
  tax_loss_box_corner: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/tjn_yellow_corner_top.png)`,
    backgroundPosition: "left top",
    backgroundRepeat: "no-repeat",
  },
  ranking_box: {
    backgroundColor: "rgba(80,128,94,1)",
    color: "#fff",
    "& h4": {
      color: "#fff",
    },
    "& h5": {
      color: "#fff",
      fontSize: "1.4rem",
    },
  },
  social_impact_box: {
    backgroundColor: "rgba(173,117,108,1)",
    color: "#fff",
  },
  harm_box: {
    "& h4": {
      color: "#AD756C",
    },
    "& h6": {
      color: "#AD756C",
    },
  },
  vulnerability_box: {
    "& h4": {
      color: "#AE8D6C",
    },
    "& h5": {
      color: "#AE8D6C",
    },
  },
  trading_box: {
    backgroundColor: "rgba(174,141,108,1)",
    // backgroundImage: `url(${process.env.PUBLIC_URL}/tjn_yellow_corner_bottom.png)`,
    // backgroundPosition: 'right bottom',
    // backgroundRepeat: 'no-repeat',
    "& h4": {
      color: "#fff",
      fontSize: "1.4rem",
      textAlign: "center",
    },
    "& h6": {
      color: "#fff",
      fontSize: "1rem",
      fontWeight: "bold",
      marginBottom: "5px",
    },
  },
  trading_box_corner: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/tjn_yellow_corner_bottom.png)`,
    backgroundPosition: "right bottom",
    backgroundRepeat: "no-repeat",
  },
  footer: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/tjn_yellow_corner_bottom.png)`,
    backgroundPosition: "right bottom",
    backgroundRepeat: "no-repeat",
    borderTop: "2px solid rgba(174,141,108,1)",
    padding: "10px  !important",
  },
  center_elements: {
    textAlign: "center",
  },
  box_title: {
    fontSize: "1.4rem",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  sub_title: {
    fontSize: "1.3rem",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  hightlighted: {
    fontWeight: "bold",
    padding: "0px",
    fontSize: "1.2rem",
    display: "block",
    marginBottom: "0px",
  },
  elements_margin: {
    marginBottom: "30px",
  },
  viz_no_margin: {
    marginBottom: "0px",
  },
  corporate_tax_additional_info: {
    marginBottom: "0.5rem",
    marginLeft: "0.5rem",
  },
});

const formatLargeNum = (num) => {
  if (num >= 1e12) {
    return format(",")(Math.round(num / 1e12)) + " trillion";
  } else if (num >= 1e9) {
    return format(",")(Math.round(num / 1e9)) + " billion";
  } else if (num >= 1e6) {
    return format(",")(Math.round(num / 1e6)) + " million";
  } else {
    return format(",")(Math.round(num));
  }

  // const numString = format('0.1s')(num);

  // return numString.replace(".","").replace(/G/," billion").replace(/M/," million").replace(/T/," trillion").replace(/k/,",000")
};

const getCBCRInfo = (sourceName, cbcr) => {
  if (sourceName === "self") {
    if (cbcr === "Yes") return "rgba(80,128,94,1)";
    else if (cbcr === "No") return "#F44336";
    else return "#BDBDBD";
  } else if (sourceName === "others") {
    if (cbcr === "Low") return "#c6eccf";
    else if (cbcr === "Fair") return "#8ab595";
    else if (cbcr === "High") return "#50805e";
  }
};

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    "& a": {
      color: "#fff",
      textDecoration: "underline",
    },
    fontSize: "0.8rem",
  },
}))(Tooltip);

const infoBoxes = {
  ...infoBoxDescriptions,
  tax_loss_box: {
    ...infoBoxDescriptions.tax_loss_box,
    color: "rgba(80,128,94,1)",
  },
  ranking_box: {
    ...infoBoxDescriptions.ranking_box,
    color: "#fff",
  },
  social_impact_box: {
    ...infoBoxDescriptions.social_impact_box,
    color: "#fff",
  },
  harm_box: {
    ...infoBoxDescriptions.harm_box,
    color: "#AD756C",
  },
  vulnerability_box: {
    ...infoBoxDescriptions.vulnerability_box,
    color: "#AE8D6C",
  },
  trading_box: {
    ...infoBoxDescriptions.trading_box,
    color: "#fff",
  },
};

const willShowBox = (boxName, boxToShow) => {
  // if (!summaryBoxes.includes(boxToShow)) {
  //   return true;
  // } else if (boxName === boxToShow) return true;
  // else return false;
  return summaryBoxes.includes(boxName);
};

const getBoxWidth = (boxName, boxToShow) => {
  if (boxName === boxToShow) return 12;
  else return 6;
};

export default (props) => {
  const sumProfile = props.sumProfile && props.sumProfile.length > 0 ? extractTradingPartnerInfo(props.sumProfile[0]) : [];
  const allProfiles = props.allProfiles;

  const currentYear = +props.year;

  const { showTitle, boxToShow, showFooter } = props;

  const [boxRefTaxLoss, boxSizeTaxLoss] = useDimensions();
  const [boxRefRankingCTHI, boxSizeRankingCTHI] = useDimensions();
  const [boxRefRankingFSI, boxSizeRankingFSI] = useDimensions();
  // const [boxRefSocialImpact, boxSizeSocialImpact] = useDimensions();
  const [boxRefHarm, boxSizeHarm] = useDimensions();
  const [boxSubSectionRef, boxSubSectionSize] = useDimensions();

  const classes = useStyles();

  // console.log(currentYear);

  return (
    <Grid container spacing={2} style={{ padding: "0px 10px" }}>
      {showTitle ? (
        <Grid item xs={12} sm={12} className={classes.title_box}>
          {/* <img
            alt={'flag of ' + sumProfile['iso2']}
            src={
              'https://www.countryflags.io/' +
              sumProfile['iso2'].toLowerCase() +
              '/flat/64.png'
            }
          /> */}
          {/* <span class={"flag-icon flag-icon-"+sumProfile['iso2'].toLowerCase()}></span> */}
          <img class="flag-img" src={"flags/" + sumProfile["iso2"].toLowerCase() + ".svg"} alt={"flag of " + sumProfile["iso2"]} height={50} />
          <Typography variant="h3" color="primary">
            {sumProfile["country_name"]}
          </Typography>
          <Typography variant="h5" color="primary" style={{ display: "block" }}>
            {currentYear}
          </Typography>
        </Grid>
      ) : null}

      {/* Tax Loss Box */}
      {willShowBox("tax_loss_box", boxToShow) ? (
        <Grid item xs={12} sm={getBoxWidth("tax_loss_box", boxToShow)} className={showTitle ? classes.box : classes.box + ` ` + classes.tax_loss_box_corner}>
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.tax_loss_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.tax_loss_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.elements_margin}>
            <Grid item xs>
              <Typography variant="h3" style={{ paddingTop: "0px" }} color="primary">
                {format("$,d")(Math.abs(sumProfile["total_tax_loss"]))}
              </Typography>
              <Typography variant="body">lost in tax every year to global tax abuse</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs>
              <Typography variant="body" style={{ paddingTop: "0px" }}>
                Equivalent to{" "}
              </Typography>
              <Typography variant="h4">
                <Icon icon="ant-design:line-chart-outlined" color="#AE8D6C" width="30" height="30" />
              </Typography>
              <Typography variant="h4" className={classes.hightlighted} color="primary">
                {format(",.2")(sumProfile["gdp_loss"])}% of {sumProfile["revenue_or_gdp_1"]}
              </Typography>
              <Typography variant="body" style={{ paddingTop: "0px" }}>
                ({capitaliseFirstLetter(sumProfile["revenue_or_gdp_1"])} : {"$" + formatLargeNum(sumProfile["country_gdp"])})
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography variant="body" style={{ paddingTop: "0px" }}>
                Equivalent to loss of
              </Typography>
              <Typography variant="h4">
                <Icon icon="mdi:human-male" color="#AE8D6C" width="30" height="30" />
              </Typography>
              <Typography variant="h4" className={classes.hightlighted} color="primary">
                ${formatLargeNum(sumProfile["loss_per_capita"])} per member
              </Typography>
              <Typography variant="body" style={{ paddingTop: "0px" }}>
                of population
              </Typography>
              <br />
              <Typography variant="body" style={{ paddingTop: "0px" }}>
                (Population: {formatLargeNum(sumProfile["country_population"])})
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.viz_no_margin} ref={boxRefTaxLoss}>
            <Grid item xs>
              <SummaryProfileLineMarker
                boxClass="tax_loss_box"
                classes={classes}
                color={{ primary: "rgba(80,128,94,1)", secondary: "#AE8D6C" }}
                size={boxSizeTaxLoss}
                markers={[
                  {
                    key: "Percentage of GDP Loss",
                    value: sumProfile["gdp_loss"],
                    pointerPos: "top-left",
                  },
                  {
                    key: "Global Average GDP Loss",
                    value: sumProfile["global_gdp_loss"],
                    pointerPos: "bottom-left",
                  },
                  {
                    key: "Regional Average GDP Loss",
                    value: sumProfile["regional_gdp_loss"],
                    pointerPos: "bottom-right",
                  },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.elements_margin}>
            <Grid item xs>
              {sumProfile["gdp_loss"] >= sumProfile["global_gdp_loss"] ? (
                <>
                  {/* <Typography variant="h4">
										<Icon icon="mdi:greater-than" color="#AE8D6C" width="30" height="30" />
									</Typography> */}
                  <Typography variant="h4" className={classes.hightlighted} color="primary">
                    greater than
                  </Typography>
                </>
              ) : (
                <>
                  {/* <Typography variant="h4">
										<Icon icon="mdi:less-than" color="#AE8D6C" width="30" height="30" /> 
									</Typography> */}
                  <Typography variant="h4" className={classes.hightlighted} color="primary">
                    less than
                  </Typography>
                </>
              )}
              <Typography variant="body">
                Global Average {capitaliseFirstLetter(sumProfile["revenue_or_gdp_1"])} Loss {format(",.2")(sumProfile["global_gdp_loss"])}%
              </Typography>
            </Grid>
            <Grid item xs>
              {sumProfile["gdp_loss"] >= sumProfile["regional_gdp_loss"] ? (
                <>
                  {/* <Typography variant="h4">
											<Icon icon="mdi:greater-than" color="#AE8D6C" width="30" height="30" />
										</Typography> */}
                  <Typography variant="h4" className={classes.hightlighted} color="primary">
                    greater than
                  </Typography>
                </>
              ) : (
                <>
                  {/* <Typography variant="h4">
											<Icon icon="mdi:less-than" color="#AE8D6C" width="30" height="30" /> 
										</Typography> */}
                  <Typography variant="h4" className={classes.hightlighted} color="primary">
                    less than
                  </Typography>
                </>
              )}
              <Typography variant="body">
                Regional Average {capitaliseFirstLetter(sumProfile["revenue_or_gdp_1"])} Loss of {format(",.2")(sumProfile["regional_gdp_loss"])}%
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="h6" color="primary" className={classes.sub_title}>
            Breakdown of tax abuse losses
          </Typography>
          <Grid container spacing={0} className={classes.viz_no_margin}>
            <Grid item xs>
              <SummaryStackedBarChart
                boxClass="tax_loss_box"
                classes={classes}
                color={{
                  primary: "rgba(80,128,94,1)",
                  secondary: "#AE8D6C",
                  border: "#fff",
                }}
                size={boxSizeTaxLoss}
                markers={[
                  {
                    key: "Lost to tax avoidance",
                    value: sumProfile["tax_avoidance_loss"],
                    pointerPos: "bottom-left",
                  },
                  {
                    key: "Lost to tax evasion",
                    value: sumProfile["tax_evasion_loss"],
                    pointerPos: "bottom-right",
                  },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.elements_margin}>
            <Grid item xs>
              <Icon icon="maki:building-alt1-11" color="#AE8D6C" width="30" height="30" />
              <Typography variant="h4" className={classes.hightlighted} color="primary">
                ${formatLargeNum(sumProfile["tax_avoidance_loss"])}
                <span class={classes.corporate_tax_additional_info}>
                  <StyledTooltip
                    interactive
                    title={
                      <span>
                        CBCR data available from country? <b>{sumProfile["cbcr_country"]}</b>
                      </span>
                    }
                    placement="top"
                  >
                    <Typography variant="p">
                      <Icon icon="bi-circle-fill" color={getCBCRInfo("self", sumProfile["cbcr_country"])} width="10" height="10" />
                    </Typography>
                  </StyledTooltip>
                </span>
                <span class={classes.corporate_tax_additional_info}>
                  <StyledTooltip
                    interactive
                    title={
                      <span>
                        Availability of CBCR data covering country from other countries? <b>{sumProfile["cbcr_others"]}</b>
                      </span>
                    }
                    placement="top"
                  >
                    <Typography variant="p">
                      <Icon icon="bi-circle-fill" color={getCBCRInfo("others", sumProfile["cbcr_others"])} width="10" height="10" />
                    </Typography>
                  </StyledTooltip>
                </span>
              </Typography>
              <Typography variant="body">lost to global tax abuse committed by multinational corporations.</Typography>
            </Grid>
            <Grid item xs>
              <Icon icon="la:user-tie-solid" color="#AE8D6C" width="30" height="30" />
              <Typography variant="h4" className={classes.hightlighted} color="primary">
                ${formatLargeNum(sumProfile["tax_evasion_loss"])}
              </Typography>
              <Typography variant="body">lost to global tax evasion committed by private individuals.</Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
      {/* Tax Loss Box */}

      {/* Global Ranking */}
      {willShowBox("ranking_box", boxToShow) ? (
        <Grid item xs={12} sm={getBoxWidth("ranking_box", boxToShow)} className={`${classes.box} ${classes.ranking_box}`}>
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.ranking_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.ranking_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Typography variant="h4" color="warning" className={classes.box_title}>
            Global Rankings
          </Typography>
          <Typography variant="h6" color="warning" className={classes.sub_title}>
            Corporate Tax Haven Index {sumProfile["year_cthi"]}
          </Typography>
          <Grid container spacing={0} className={classes.viz_no_margin} ref={boxRefRankingCTHI}>
            <Grid item xs>
              <SummaryRankingBarChart
                boxClass="ranking_box"
                classes={classes}
                color={{
                  primary: "#fff",
                  secondary: "rgba(173,117,108,1)",
                  highlight: "#AE8D6C",
                }}
                size={boxSizeRankingCTHI}
                data={allProfiles.map((profile) => {
                  return {
                    index: profile["iso3"],
                    key: "CTHI Share",
                    value: +profile["cthi_share"],
                    rank: +profile["cthi_rank"],
                    name: profile["country_name"],
                  };
                })}
                highlight={[
                  {
                    index: sumProfile["iso3"],
                    key: "CTHI Share",
                    value: +sumProfile["cthi_share"],
                    pointerPos: "bottom-left",
                  },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements} style={{ marginBottom: "50px" }}>
            <Grid item xs>
              <Typography variant="h6" className={classes.hightlighted}>
                #{sumProfile["cthi_rank"]}
              </Typography>
              <Typography variant="body">
                Global Rank <br />
                (First is worst)
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography variant="h6" className={classes.hightlighted}>
                {format("d")(sumProfile["cthi_score"])}/100
              </Typography>
              <Typography variant="body">
                Haven Score <br />
                (100 is worst)
              </Typography>
            </Grid>
            <Grid container spacing={0} className={classes.center_elements}>
              <Grid item xs>
                <Typography variant="h6" className={classes.hightlighted}>
                  {format(",.2")(sumProfile["cthi_share"] * 100)}%
                </Typography>
                <Typography variant="body">share of global tax havenry responsible for</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Typography variant="h6" className={classes.sub_title}>
            Financial Secrecy Index {sumProfile["year_fsi"]}
          </Typography>
          <Grid container spacing={0} className={classes.viz_no_margin} ref={boxRefRankingFSI}>
            <Grid item xs>
              <SummaryRankingBarChart
                boxClass="ranking_box"
                classes={classes}
                color={{
                  primary: "#fff",
                  secondary: "rgba(173,117,108,1)",
                  highlight: "#AE8D6C",
                }}
                size={boxSizeRankingFSI}
                data={allProfiles.map((profile) => {
                  return {
                    index: profile["iso3"],
                    key: "FSI Share",
                    value: +profile["fsi_share"],
                    rank: +profile["fsi_rank"],
                    name: profile["country_name"],
                  };
                })}
                highlight={[
                  {
                    index: sumProfile["iso3"],
                    key: "FSI Share",
                    value: +sumProfile["fsi_share"],
                    pointerPos: "bottom-left",
                  },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements}>
            <Grid item xs>
              <Typography variant="h6" className={classes.hightlighted}>
                #{sumProfile["fsi_rank"]}
              </Typography>
              <Typography variant="body">
                Global Rank <br />
                (First is worst)
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography variant="h6" className={classes.hightlighted}>
                {format("d")(sumProfile["fsi_score"])}/100
              </Typography>
              <Typography variant="body">
                Haven Score <br />
                (100 is worst)
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements}>
            <Grid item xs>
              <Typography variant="h6" className={classes.hightlighted}>
                {format(",.2")(sumProfile["fsi_share"] * 100)}%
              </Typography>
              <Typography variant="body">share of global financial secrecy responsible for</Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
      {/* End of Global Ranking */}

      {/* Social Impact */}
      {willShowBox("social_impact_box", boxToShow) ? (
        <Grid item xs={12} sm={getBoxWidth("social_impact_box", boxToShow)} className={`${classes.box} ${classes.social_impact_box}`}>
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.social_impact_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.social_impact_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Typography variant="h4" color="success" className={classes.box_title}>
            Social impact of lost tax
          </Typography>
          {currentYear >= 2021 ? <Typography variant="body">Tax loss enough to fully vaccinate</Typography> : <></>}
          {/*Start of Social Impact of lost tax*/}
          <Grid container spacing={0} className={`${classes.center_elements} ${classes.elements_margin}`} style={{ marginBottom: "50px" }}>
            <Grid item xs={6}>
              {currentYear >= 2021 ? (
                <>
                  <Typography variant="h4">
                    <InlineIcon icon="tabler:vaccine" color="#fff" width="30" height="30" />
                  </Typography>
                  <Typography variant="h6" className={classes.hightlighted}>
                    {format(",d")(sumProfile["vaccines"])} people
                  </Typography>
                  <Typography variant="body">againt COVID-19</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body">Tax loss equivalent to</Typography>
                  <Typography variant="h4">
                    <Icon icon="la-stethoscope-solid" color="#fff" width="30" height="30" />
                  </Typography>
                  <Grid container spacing={0} className={classes.viz_no_margin} ref={boxSubSectionRef}>
                    <Grid item xs>
                      <SummaryMultiPieChart
                        boxClass="social_impact_box"
                        classes={classes}
                        color={{
                          primary: "#fff",
                          secondary: "rgba(173,117,108,1)",
                        }}
                        size={boxSubSectionSize}
                        marker={{
                          key: "Loss as percentage of Health Budget",
                          value: sumProfile["health_expenditure"],
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="h6" className={classes.hightlighted}>
                    {format(",.2f")(sumProfile["health_expenditure"])}%
                  </Typography>
                  <Typography variant="body">of health budget</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6}>
              {currentYear >= 2021 ? (
                <>
                  <Grid container spacing={0} className={classes.viz_no_margin} ref={boxSubSectionRef}>
                    <Grid item xs>
                      <SummaryMultiPieChart
                        boxClass="social_impact_box"
                        classes={classes}
                        color={{
                          primary: "#fff",
                          secondary: "rgba(173,117,108,1)",
                        }}
                        size={{
                          ...boxSubSectionSize,
                          width: boxSubSectionSize.width / 2,
                        }}
                        marker={{
                          key: "Loss as percentage of Health Budget",
                          value: sumProfile["vaccines_pop"] <= 100 ? sumProfile["vaccines_pop"] /*Need to change value here*/ : 99,
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="h6" className={classes.hightlighted}>
                    {sumProfile["vaccines_pop"]}%
                  </Typography>
                  <Typography variant="body">
                    of population <br />
                    againt COVID-19
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body">Tax Loss equivalent to</Typography>
                  <Typography variant="h4">
                    <Icon icon="dashicons:money-alt" color="#fff" width="30" height="30" />
                  </Typography>
                  <Grid container spacing={0} className={classes.viz_no_margin} ref={boxSubSectionRef}>
                    <Grid item xs>
                      <SummaryMultiPieChart
                        boxClass="social_impact_box"
                        classes={classes}
                        color={{
                          primary: "#fff",
                          secondary: "rgba(173,117,108,1)",
                        }}
                        size={boxSubSectionSize}
                        marker={{
                          key: "Loss as percentage of Education Budget",
                          value: sumProfile["education_expenditure"],
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="h6" className={classes.hightlighted}>
                    {format(",.2f")(sumProfile["education_expenditure"])}%
                  </Typography>
                  <Typography variant="body">of education spending</Typography>
                </>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements} style={{ marginBottom: "20px" }}>
            {currentYear >= 2021 ? (
              <Grid item xs>
                <Typography variant="body">Tax loss equivalent to</Typography>
                <Typography variant="h4">
                  <Icon icon="la-stethoscope-solid" color="#fff" width="30" height="30" />
                </Typography>
                <Grid container spacing={0} className={classes.viz_no_margin} ref={boxSubSectionRef}>
                  <Grid item xs>
                    <SummaryMultiPieChart
                      boxClass="social_impact_box"
                      classes={classes}
                      color={{
                        primary: "#fff",
                        secondary: "rgba(173,117,108,1)",
                      }}
                      size={boxSubSectionSize}
                      marker={{
                        key: "Loss as percentage of Health Budget",
                        value: sumProfile["health_expenditure"],
                      }}
                    />
                  </Grid>
                </Grid>
                <Typography variant="h6" className={classes.hightlighted}>
                  {format(",.2f")(sumProfile["health_expenditure"])}%
                </Typography>
                <Typography variant="body">of health budget</Typography>
              </Grid>
            ) : (
              <Grid item xs>
                {/*
                <Typography variant="body">Suffered tax loss equivalent to paying yearly salaries of</Typography>
                <Typography variant="h4">
                  <InlineIcon icon="wpf:medical-doctor" color="#fff" width="30" height="30" />
                </Typography>
                <Typography variant="h6" className={classes.hightlighted}>
                  {format(",d")(sumProfile["nurses_lost"])} nurses
            </Typography>*/}
              </Grid>
            )}

            {/* // <Grid item xs></Grid> */}
          </Grid>
        </Grid>
      ) : null}
      {/* End of Social Impact */}

      {/* Harm to other countries */}
      {willShowBox("harm_box", boxToShow) ? (
        <Grid item xs={12} sm={getBoxWidth("harm_box", boxToShow)} className={`${classes.box} ${classes.harm_box}`}>
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.harm_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.harm_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements} style={{ marginBottom: "20px" }}>
            <Grid item xs style={{ marginBottom: "10px" }}>
              <Typography variant="h4" color="#AE8D6C" className={classes.box_title}>
                Harm to other countries
              </Typography>
              <Icon icon="tabler-coin" color="#AE8D6C" width="30" height="30" />
              <Typography variant="h6" className={classes.hightlighted} color="white">
                ${formatLargeNum(sumProfile["tax_lost_others"])}
              </Typography>
              <Typography variant="body">
                tax lost inflicted on other countries by <br />
                facilitating global tax abuse{" "}
              </Typography>
            </Grid>

            <Grid container spacing={0} className={classes.viz_no_margin} ref={boxRefHarm}>
              <Grid item xs>
                <SummaryStackedBarChart
                  boxClass="harm_box"
                  classes={classes}
                  color={{
                    primary: "rgba(173,117,108,1)",
                    secondary: "#AE8D6C",
                    border: "#fff",
                  }}
                  size={boxSizeHarm}
                  markers={[
                    {
                      key: "Harm to others due to tax avoidance",
                      value: sumProfile["tax_lost_others_avoidance"],
                      pointerPos: "bottom-left",
                    },
                    {
                      key: "Harm to others due to tax evasion",
                      value: sumProfile["tax_lost_others_evasion"],
                      pointerPos: "bottom-right",
                    },
                  ]}
                />
              </Grid>
            </Grid>
            <Grid container spacing={0} className={classes.center_elements} style={{ marginBottom: "20px" }}>
              <Grid item xs>
                <Typography variant="h6" className={classes.hightlighted} color="white">
                  ${formatLargeNum(sumProfile["tax_lost_others_avoidance"])}
                </Typography>
                <Typography variant="body">by enabling global corporate tax abuse</Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" className={classes.hightlighted} color="white">
                  ${formatLargeNum(sumProfile["tax_lost_others_evasion"])}
                </Typography>
                <Typography variant="body">by enabling global private tax evasion</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={0} className={classes.center_elements} style={{ marginBottom: "20px" }}>
            <Grid item xs>
              <Typography variant="body">Responsible for</Typography>
              <Typography variant="h4">
                <Icon icon="el-globe" color="#AE8D6C" />
              </Typography>
              <Typography variant="h6" className={classes.hightlighted} color="#AE8D6C">
                {format(",.2f")(sumProfile["percent_global_loss"])}%
              </Typography>
              <Typography variant="body">of global tax losses</Typography>
            </Grid>
            <Grid item xs>
              {currentYear >= 2021 ? (
                <>
                  <Typography variant="body">Inflicted tax loss enough to fully vaccinate</Typography>
                  <Typography variant="h4">
                    <InlineIcon icon="tabler:vaccine" color="#AE8D6C" width="30" height="30" />
                  </Typography>
                  <Typography variant="h6" className={classes.hightlighted} color="white">
                    {format(",d")(sumProfile["vaccines_inflicted"])} people
                  </Typography>
                  <Typography variant="body">againt COVID-19</Typography>
                </>
              ) : (
                <>
                  {/*
                  <Typography variant="body">Inflicted tax loss equivalent to yearly salaries of</Typography>
                  <Typography variant="h4">
                    <InlineIcon icon="wpf:medical-doctor" color="#AE8D6C" width="30" height="30" />
                  </Typography>
                  <Typography variant="h6" className={classes.hightlighted} color="white">
                    {format(",d")(sumProfile["nurses_lost_others"])} nurses
                  </Typography>*/}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : null}
      {/* End of Harm to other countries */}

      {/* Vulnerability to illicit financial flows */}
      {willShowBox("vulnerability_box", boxToShow) ? (
        <Grid item xs={12} sm={getBoxWidth("vulnerability_box", boxToShow)} className={`${classes.box} ${classes.vulnerability_box}`}>
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.vulnerability_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.vulnerability_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Typography variant="h4" color="warning" className={classes.box_title}>
            Vulnerability to illicit financial flows
          </Typography>
          <Grid container spacing={0} className={classes.center_elements}>
            <Grid item xs>
              <Typography variant="body">
                Most Vulnerable to <br />
                <Typography variant="h5" color="warning" className={classes.hightlighted}>
                  {sumProfile["economic_channel"]}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography variant="body">Vulnerability Score for this channel is</Typography>
              <Typography variant="h5" className={classes.hightlighted}>
                {sumProfile["vulnerability_score"]}/100
              </Typography>
              <Typography variant="body">(100 is Worst)</Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
      {/* End of Vulnerability to illicit financial flows */}

      {/* Top 3 Most Vulnerable Trading Partners Box */}
      {willShowBox("trading_box", boxToShow) ? (
        <Grid
          item
          xs={12}
          sm={getBoxWidth("trading_box", boxToShow)}
          className={showFooter ? classes.box + ` ` + classes.trading_box : classes.box + ` ` + classes.trading_box + ` ` + classes.trading_box_corner}
        >
          <Grid container spacing={0}>
            <Grid xs={10}></Grid>
            <Grid xs={2} align="right">
              <StyledTooltip interactive title={infoBoxes.trading_box.text} placement="top">
                <Typography variant="p">
                  <Icon icon="ant-design:info-circle-filled" color={infoBoxes.trading_box.color} width="20" height="20" />
                </Typography>
              </StyledTooltip>
            </Grid>
          </Grid>
          <Typography variant="h4" color="warning" className={classes.hightlighted}>
            Trading partners most responsible for vulnerability
          </Typography>
          <Grid container spacing={0} className={classes.center_elements} style={{ marginTop: "20px" }}>
            {sumProfile["trading_partner_1_iso2"] ? (
              <Grid item xs>
                <img
                  class="flag-img"
                  src={"flags/" + sumProfile["trading_partner_1_iso2"].toLowerCase() + ".svg"}
                  alt={"flag of " + sumProfile["trading_partner_1_name"]}
                  height={30}
                />
                {/* <img
                  alt={'flag of ' + sumProfile['trading_partner_1_name']}
                  src={
                    'https://www.countryflags.io/' +
                    sumProfile['trading_partner_1_iso2'].toLowerCase() +
                    '/flat/32.png'
                  }
                /> */}
                <Typography variant="h6">{sumProfile["trading_partner_1_name_only"]}</Typography>
              </Grid>
            ) : null}
            {sumProfile["trading_partner_2_iso2"] ? (
              <Grid item xs>
                <img
                  class="flag-img"
                  src={"flags/" + sumProfile["trading_partner_2_iso2"].toLowerCase() + ".svg"}
                  alt={"flag of " + sumProfile["trading_partner_2_name"]}
                  height={30}
                />
                {/* <img
                  alt={'flag of ' + sumProfile['trading_partner_2_name']}
                  src={
                    'https://www.countryflags.io/' +
                    sumProfile['trading_partner_2_iso2'].toLowerCase() +
                    '/flat/32.png'
                  }
                /> */}
                <Typography variant="h6">{sumProfile["trading_partner_2_name_only"]}</Typography>
              </Grid>
            ) : null}
            {sumProfile["trading_partner_2_iso2"] ? (
              <Grid item xs>
                <img
                  class="flag-img"
                  src={"flags/" + sumProfile["trading_partner_3_iso2"].toLowerCase() + ".svg"}
                  alt={"flag of " + sumProfile["trading_partner_3_name"]}
                  height={30}
                />
                {/* <img
                  alt={'flag of ' + sumProfile['trading_partner_3_name']}
                  src={
                    'https://www.countryflags.io/' +
                    sumProfile['trading_partner_3_iso2'].toLowerCase() +
                    '/flat/32.png'
                  }
                /> */}
                <Typography variant="h6">{sumProfile["trading_partner_3_name_only"]}</Typography>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      ) : null}
      {/* Top 3 Most Vulnerable Trading Partners Box */}

      {showFooter ? (
        <Grid item xs={12} sm={12} className={`${classes.box} ${classes.footer}`} align="center">
          <Grid item xs={6} sm={3}>
            <Link href="http://taxjustice.net/" target="_blank" rel="noopener noreferrer">
              <img src={`${process.env.PUBLIC_URL}/Tax_Justice_Network_Logo.svg`} alt="Tax Justice Network Logo" width="100%" />
            </Link>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};
